/* eslint arrow-body-style: 0 */
import ConversationView from './ConversationView.vue';
import { frontendURL } from '../../../helper/URLHelper';
import {
  dispatchAccountPreference,
  isExplicitAllViewQuery,
  resolveLastConversationView,
} from '../../../helper/userPreferencesHelper';
import store from '../../../store';

const CONVERSATIONS_SECTION_ROUTES = [
  'home',
  'inbox_dashboard',
  'inbox_conversation',
  'conversation_through_inbox',
  'notifications_dashboard',
  'label_conversations',
  'conversations_through_label',
  'team_conversations',
  'conversations_through_team',
  'conversation_mentions',
  'conversation_through_mentions',
  'conversation_participating',
  'conversation_through_participating',
  'folder_conversations',
  'conversations_through_folders',
  'conversation_unattended',
  'conversation_through_unattended',
];

async function clearLastConversationView(accountId) {
  await dispatchAccountPreference(store, accountId, 'last_conversation_view', {
    type: 'home',
  });
}

async function resolveSavedConversationView(accountId, lastConversationView) {
  let folders = store.getters['customViews/getCustomViewsByFilterType'](
    'conversation'
  );
  let labels = store.getters['labels/getLabels'];
  let inboxes = store.getters['inboxes/getInboxes'];
  let teams = store.getters['teams/getTeams'];

  if (lastConversationView.type === 'folder') {
    await store.dispatch('customViews/get', 'conversation');
    folders = store.getters['customViews/getCustomViewsByFilterType'](
      'conversation'
    );
  }

  if (lastConversationView.type === 'label') {
    try {
      await store.dispatch('labels/getFresh');
      labels = store.getters['labels/getLabels'];
    } catch {
      return { valid: true, view: { type: 'home' } };
    }
  }

  if (lastConversationView.type === 'inbox' && !inboxes.length) {
    await store.dispatch('inboxes/get');
    inboxes = store.getters['inboxes/getInboxes'];
  }

  if (lastConversationView.type === 'team' && !teams.length) {
    await store.dispatch('teams/get');
    teams = store.getters['teams/getTeams'];
  }

  return resolveLastConversationView(lastConversationView, {
    folders,
    labels,
    inboxes,
    teams,
  });
}

function conversationViewPath(accountId, view) {
  switch (view.type) {
    case 'folder':
      if (view.id) {
        return frontendURL(`accounts/${accountId}/custom_view/${view.id}`);
      }
      break;
    case 'inbox':
      if (view.id) {
        return frontendURL(`accounts/${accountId}/inbox/${view.id}`);
      }
      break;
    case 'label':
      if (view.id) {
        return frontendURL(`accounts/${accountId}/label/${view.id}`);
      }
      break;
    case 'team':
      if (view.id) {
        return frontendURL(`accounts/${accountId}/team/${view.id}`);
      }
      break;
    case 'mention':
      return frontendURL(`accounts/${accountId}/mentions/conversations`);
    case 'unattended':
      return frontendURL(`accounts/${accountId}/unattended/conversations`);
    case 'participating':
      return frontendURL(`accounts/${accountId}/participating/conversations`);
    default:
      break;
  }

  return null;
}

export default {
  routes: [
    {
      path: frontendURL('accounts/:accountId/dashboard'),
      name: 'home',
      roles: ['administrator', 'agent'],
      component: ConversationView,
      beforeEnter: async (to, from, next) => {
        const { accountId } = to.params;

        if (isExplicitAllViewQuery(to)) {
          await dispatchAccountPreference(
            store,
            accountId,
            'last_conversation_view',
            { type: 'home' }
          );
          next({ name: to.name, params: to.params, query: {}, replace: true });
          return;
        }

        const currentUser = store.getters.getCurrentUser;
        const userPrefs = currentUser?.ui_settings?.user_preferences_by_account;
        const lastConversationView = userPrefs?.[accountId]?.last_conversation_view;
        const isExplicitAllConversationsNavigation =
          from?.name && CONVERSATIONS_SECTION_ROUTES.includes(from.name);

        // Restore last view when entering Conversations from another section.
        // Skip restore when user clicks "All Conversations" while already in Conversations.
        if (
          !isExplicitAllConversationsNavigation &&
          lastConversationView &&
          lastConversationView.type &&
          lastConversationView.type !== 'home'
        ) {
          const { valid, view } = await resolveSavedConversationView(
            accountId,
            lastConversationView
          );

          if (!valid) {
            await clearLastConversationView(accountId);
            next();
            return;
          }

          const targetPath = conversationViewPath(accountId, view);
          if (targetPath && targetPath !== to.path) {
            next({ path: targetPath });
            return;
          }
        }

        next();
      },
      props: () => {
        return { inboxId: 0 };
      },
    },
    {
      path: frontendURL('accounts/:accountId/conversations/:conversation_id'),
      name: 'inbox_conversation',
      roles: ['administrator', 'agent'],
      component: ConversationView,
      props: route => {
        return { inboxId: 0, conversationId: route.params.conversation_id };
      },
    },
    {
      path: frontendURL('accounts/:accountId/inbox/:inbox_id'),
      name: 'inbox_dashboard',
      roles: ['administrator', 'agent'],
      component: ConversationView,
      props: route => {
        return { inboxId: route.params.inbox_id };
      },
    },
    {
      path: frontendURL(
        'accounts/:accountId/inbox/:inbox_id/conversations/:conversation_id'
      ),
      name: 'conversation_through_inbox',
      roles: ['administrator', 'agent'],
      component: ConversationView,
      props: route => {
        return {
          conversationId: route.params.conversation_id,
          inboxId: route.params.inbox_id,
        };
      },
    },
    {
      path: frontendURL('accounts/:accountId/label/:label'),
      name: 'label_conversations',
      roles: ['administrator', 'agent'],
      component: ConversationView,
      props: route => ({ label: route.params.label }),
    },
    {
      path: frontendURL(
        'accounts/:accountId/label/:label/conversations/:conversation_id'
      ),
      name: 'conversations_through_label',
      roles: ['administrator', 'agent'],
      component: ConversationView,
      props: route => ({
        conversationId: route.params.conversation_id,
        label: route.params.label,
      }),
    },
    {
      path: frontendURL('accounts/:accountId/team/:teamId'),
      name: 'team_conversations',
      roles: ['administrator', 'agent'],
      component: ConversationView,
      props: route => ({ teamId: route.params.teamId }),
    },
    {
      path: frontendURL(
        'accounts/:accountId/team/:teamId/conversations/:conversationId'
      ),
      name: 'conversations_through_team',
      roles: ['administrator', 'agent'],
      component: ConversationView,
      props: route => ({
        conversationId: route.params.conversationId,
        teamId: route.params.teamId,
      }),
    },
    {
      path: frontendURL('accounts/:accountId/custom_view/:id'),
      name: 'folder_conversations',
      roles: ['administrator', 'agent'],
      component: ConversationView,
      props: route => ({ foldersId: route.params.id }),
    },
    {
      path: frontendURL(
        'accounts/:accountId/custom_view/:id/conversations/:conversation_id'
      ),
      name: 'conversations_through_folders',
      roles: ['administrator', 'agent'],
      component: ConversationView,
      props: route => ({
        conversationId: route.params.conversation_id,
        foldersId: route.params.id,
      }),
    },
    {
      path: frontendURL('accounts/:accountId/mentions/conversations'),
      name: 'conversation_mentions',
      roles: ['administrator', 'agent'],
      component: ConversationView,
      props: () => ({ conversationType: 'mention' }),
    },
    {
      path: frontendURL(
        'accounts/:accountId/mentions/conversations/:conversationId'
      ),
      name: 'conversation_through_mentions',
      roles: ['administrator', 'agent'],
      component: ConversationView,
      props: route => ({
        conversationId: route.params.conversationId,
        conversationType: 'mention',
      }),
    },
    {
      path: frontendURL('accounts/:accountId/unattended/conversations'),
      name: 'conversation_unattended',
      roles: ['administrator', 'agent'],
      component: ConversationView,
      props: () => ({ conversationType: 'unattended' }),
    },
    {
      path: frontendURL(
        'accounts/:accountId/unattended/conversations/:conversationId'
      ),
      name: 'conversation_through_unattended',
      roles: ['administrator', 'agent'],
      component: ConversationView,
      props: route => ({
        conversationId: route.params.conversationId,
        conversationType: 'unattended',
      }),
    },
    {
      path: frontendURL('accounts/:accountId/participating/conversations'),
      name: 'conversation_participating',
      roles: ['administrator', 'agent'],
      component: ConversationView,
      props: () => ({ conversationType: 'participating' }),
    },
    {
      path: frontendURL(
        'accounts/:accountId/participating/conversations/:conversationId'
      ),
      name: 'conversation_through_participating',
      roles: ['administrator', 'agent'],
      component: ConversationView,
      props: route => ({
        conversationId: route.params.conversationId,
        conversationType: 'participating',
      }),
    },
  ],
};
